import { CardSkeleton } from "@/components/atoms/CardSkeleton/CardSkeleton";
import { ItemCard } from "@/components/molecules/ItemCard";
import { Button } from "@/components/ui/button";
import { useOrgRole } from "@/hooks/useOrgRole";
import { Policy } from "@/schemas/firestore/policy.schema";
import { Plus } from "lucide-react";

interface PolicySectionProps {
  policies: Policy[];
  selectedPolicies: string[];
  isLoadingPolicies: boolean;
  onAddPolicy: () => void;
  onEditPolicy: (id: string) => void;
  onDeletePolicy: (id: string) => void;
  onTogglePolicy: (id: string) => void;
}

export function PolicySection({
  policies,
  selectedPolicies,
  isLoadingPolicies,
  onAddPolicy,
  onEditPolicy,
  onDeletePolicy,
  onTogglePolicy,
}: PolicySectionProps) {
  // Policies are org-owned and editor-writable per firestore.rules; viewers see
  // them read-only.
  const { canEdit } = useOrgRole();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1">
          <h3 className="text-lg font-medium">Policies</h3>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onAddPolicy}>
              <Plus className="h-4 w-4 mr-2" />
              Add New
            </Button>
          </div>
        )}
      </div>

      {isLoadingPolicies ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Loading policies...</p>
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : policies.length === 0 ? (
        <p className="text-sm text-muted-foreground">No policies created yet</p>
      ) : (
        <div className="space-y-4">
          {policies.map((policy) => (
            <ItemCard
              key={policy.id}
              id={policy.id}
              title={policy.name || "Policy"}
              description={policy.description}
              createdBy={policy.created_by}
              updatedBy={policy.updated_by}
              isSelected={selectedPolicies.includes(policy.id)}
              onToggle={canEdit ? onTogglePolicy : undefined}
              onEdit={canEdit ? onEditPolicy : undefined}
              onDelete={canEdit ? onDeletePolicy : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
